import { lazy } from 'react';
import { logger } from 'services';

/**
 * Wrapper around React.Lazy.
 * Refresh the page when there is a chunk load error
 * Throws error when the error persists even after refresh
 * @param  {} componentImport
 */
export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageRefreshed = JSON.parse(
      window.localStorage.getItem('page-refreshed') || 'false',
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem('page-refreshed', 'false');

      return component;
    } catch (error) {
      logger.error('Error occured when trying to load chunk', { error });
      // reload the page only if the page has not been already reloaded
      if (!pageRefreshed) {
        window.localStorage.setItem('page-refreshed', 'true');

        return window.location.reload();
      }

      // throw the error since the page has been already reloaded
      throw error;
    }
  });

const retryForImport = async (s: number, fn: () => Promise<any>) => {
  const wait = () => new Promise((res) => setTimeout(res, s * 1000));

  await wait();

  return fn();
};

const exponentRetry = (
  exponent: number,
  fn: () => Promise<any>,
  retryAfterSecond: number,
) =>
  retryForImport(retryAfterSecond ** exponent, () => {
    return fn();
  });

export const exponentialRetryForImport = async (
  fn: () => Promise<any>,
  retryTimes = 3,
  retryAfterSecond = 3,
): Promise<void> => {
  let retryCount = 0;

  let retry = true;

  while (retry) {
    if (retryCount >= retryTimes) {
      retry = false;
    }
    // eslint-disable-next-line no-await-in-loop
    await exponentRetry(retryCount, fn, retryAfterSecond);
    retryCount += 1;
  }
};
